import { ScreenHeader } from "@/components/common/ScreenHeader";
import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { EquipmentItem } from "@/components/equipment-item";
import { ThemedText } from "@/components/themed-text";
import { BrandColors, Colors } from "@/constants/theme";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import vehicleService from "@/lib/services/vehicles";
import { showToast } from "@/lib/toast";
import {
  EQUIPMENT_OPTIONS,
  EditVehicleData,
  SERVICES_OPTIONS,
  VehicleData,
  editVehicleSchema,
} from "@/lib/validation/vehicle";
import { zodResolver } from "@hookform/resolvers/zod";
import Fontisto from "@react-native-vector-icons/fontisto";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Stack, useLocalSearchParams, useRouter } from "expo-router";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
  useColorScheme,
} from "react-native";
import { HelperText } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

export default function EditVehicleScreen() {
  const { id, vehicle } = useLocalSearchParams<{
    id: string;
    vehicle: string;
  }>();
  const vehicleData: VehicleData = JSON.parse(vehicle);
  const colorScheme = useColorScheme();
  const theme = Colors[colorScheme ?? "light"];
  const router = useRouter();
  const desktop = useDesktopLayout();

  const form = useForm<EditVehicleData>({
    resolver: zodResolver(editVehicleSchema),
    defaultValues: {
      registration_number: vehicleData?.registration_number,
      make: vehicleData?.make,
      model: vehicleData?.model,
      year: vehicleData?.year,
      vehicle_type: vehicleData?.vehicle_type,
      services_enabled: vehicleData?.services_enabled,
      equipment: vehicleData?.equipment,
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = form;

  // const registrationNumber = watch("registration_number");

  // const {
  //     vehicleCheckData,
  //     checkingVehicle,
  //     vehicleCheckError,
  //     vehicleCategory,
  // } = useVehicleCheck(registrationNumber);

  // useEffect(() => {
  //     if (vehicleCheckData) {
  //         setValue("make", vehicleCheckData.make?.toLowerCase() as string);
  //         setValue("model", vehicleCheckData.model?.toLowerCase() as string);
  //         setValue("year", vehicleCheckData.year as number);
  //         setValue("vin", vehicleCheckData.vin as string);
  //         setValue('mileage', vehicleData.mileage as string)
  //         setValue("vehicle_type", vehicleCheckData.vehicle_category as VehicleCategory);
  //     }
  // }, [vehicleCheckData]);

  const onSubmit = async (data: EditVehicleData) => {
    try {
      await vehicleService.updateVehicle(Number(id), data);

      showToast.success("Vehicle updated successfully");

      // Navigate back after a short delay
      setTimeout(() => {
        router.replace("/vehicles");
      }, 1000);
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to update vehicle",
      );
    }
  };

  return (
    <DesktopAppFrame>
    <SafeAreaView
      style={[styles.safeArea, { backgroundColor: theme.background }]}
      edges={[]}
    >
      <Stack.Screen
        options={{
          title: "Edit Vehicle",
          headerShown: !desktop,
          headerStyle: {
            backgroundColor: theme.background,
          },
          headerTintColor: theme.text,
          headerBackTitle: "Back",
        }}
      />
      {desktop && <ScreenHeader title="Edit Vehicle" showMenu={false} />}
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.container}
      >
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[
            styles.scrollContent,
            desktop && styles.desktopContent,
          ]}
          keyboardShouldPersistTaps="handled"
        >
          {/* <View style={styles.section}>
                        <ThemedText style={styles.sectionTitle} type="subtitle">
                            Vehicle Details{" "}
                            <Fontisto name="asterisk" size={10} color={BrandColors.error} />
                        </ThemedText>
                        <Controller
                            control={control}
                            name="registration_number"
                            render={({ field: { value, onChange } }) => (
                                <View>
                                    <VehicleRegistrationInput
                                        registration={value}
                                        onChangeRegistration={onChange}
                                        isChecking={checkingVehicle}
                                        error={vehicleCheckError}
                                        vehicleData={vehicleCheckData}
                                        vehicleCategory={vehicleCategory}
                                    />
                                    {errors.registration_number && (
                                        <HelperText type="error" style={{ fontSize: 8 }}>
                                            {errors.registration_number?.message}
                                        </HelperText>
                                    )}
                                </View>
                            )}
                        />
                    </View> */}

          <View style={styles.section}>
            <ThemedText style={styles.sectionTitle} type="subtitle">
              Offered Services{" "}
              <Fontisto name="asterisk" size={10} color={BrandColors.error} />
            </ThemedText>

            <View
              style={[
                styles.equipmentContainer,
                {
                  backgroundColor: theme.background,
                  borderColor: theme.border,
                },
              ]}
            >
              <Controller
                control={control}
                name="services_enabled"
                render={({ field: { value, onChange } }) => (
                  <>
                    {SERVICES_OPTIONS.map((service, index) => (
                      <EquipmentItem
                        key={service.value}
                        label={service.label}
                        value={(value || []).includes(service.value)}
                        onValueChange={(checked) => {
                          const current = value || [];
                          const newValue = checked
                            ? [...current, service.value]
                            : current.filter(
                                (v: string) => v !== service.value,
                              );
                          onChange(newValue);
                        }}
                        isLast={index === SERVICES_OPTIONS.length - 1}
                      />
                    ))}
                  </>
                )}
              />
            </View>
            {errors.services_enabled && (
              <HelperText type="error" style={{ fontSize: 12 }}>
                {errors.services_enabled.message}
              </HelperText>
            )}
          </View>

          <View style={styles.section}>
            <ThemedText style={styles.sectionTitle} type="subtitle">
              Vehicle Equipment
            </ThemedText>

            <View
              style={[
                styles.equipmentContainer,
                {
                  backgroundColor: theme.background,
                  borderColor: theme.border,
                },
              ]}
            >
              <Controller
                control={control}
                name="equipment"
                render={({ field: { value, onChange } }) => (
                  <>
                    {EQUIPMENT_OPTIONS.map((equipment, index) => (
                      <EquipmentItem
                        key={equipment.value}
                        label={equipment.label}
                        value={(value || []).includes(equipment.value)}
                        onValueChange={(checked) => {
                          const current = value || [];
                          const newValue = checked
                            ? [...current, equipment.value]
                            : current.filter(
                                (v: string) => v !== equipment.value,
                              );
                          onChange(newValue);
                        }}
                        isLast={index === EQUIPMENT_OPTIONS.length - 1}
                      />
                    ))}
                  </>
                )}
              />
            </View>
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.button, isSubmitting && styles.buttonDisabled]}
              onPress={handleSubmit(onSubmit)}
              disabled={isSubmitting}
            >
              <Ionicons name="checkmark-circle" size={20} color="#fff" />
              <ThemedText style={styles.buttonText} type="defaultSemiBold">
                {isSubmitting ? "Updating..." : "Update Vehicle"}
              </ThemedText>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.cancelButton, { borderColor: theme.border }]}
              onPress={() => router.back()}
              disabled={isSubmitting}
            >
              <ThemedText style={styles.cancelButtonText} type="small">
                Cancel
              </ThemedText>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
    </DesktopAppFrame>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 40,
  },
  desktopContent: {
    width: "100%",
    maxWidth: 720,
    alignSelf: "center",
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: "600",
    marginBottom: 16,
  },
  buttonContainer: {
    marginTop: 8,
    gap: 12,
  },
  button: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BrandColors.primary,
    padding: 16,
    borderRadius: 12,
    gap: 8,
  },
  buttonDisabled: {
    backgroundColor: "#ccc",
  },
  buttonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
  cancelButton: {
    alignItems: "center",
    padding: 16,
    borderWidth: 1,
    borderRadius: 12,
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: "500",
  },
  equipmentContainer: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 8,
  },
});
